/**
 * Client mirror of App\Support\SyncChecksum.
 *
 * Must stay bit-identical to the PHP: a checksum that disagrees for arithmetic
 * reasons rather than data reasons would report drift on every healthy cache and
 * teach everyone to ignore the signal. `syncChecksum.test.js` and
 * SyncChecksumMirrorTest replay the same fixture through both halves.
 *
 * See the PHP docblock for why FNV-1a, epoch seconds, and a commutative combine.
 */

const FNV_OFFSET_BASIS = 2166136261;
const FNV_PRIME = 16777619;
const MOD = 4294967296; // 2^32

const encoder = typeof TextEncoder !== 'undefined' ? new TextEncoder() : null;

/**
 * The same bytes PHP's `ord($s[$i])` walks.
 *
 * charCodeAt yields UTF-16 code units, so anything above U+007F would hash
 * differently on the two sides. Today every input is `id:epochSeconds` and so is
 * ASCII by construction, but a checksum that silently depends on that is a trap
 * for whoever hashes a name next.
 */
function bytesOf(text) {
    if (encoder) return encoder.encode(text);

    const out = [];
    for (let i = 0; i < text.length; i += 1) out.push(text.charCodeAt(i) & 0xff);
    return out;
}

/** FNV-1a over a string's UTF-8 bytes, as an unsigned 32-bit int. */
export function hash(value) {
    let h = FNV_OFFSET_BASIS;

    for (const byte of bytesOf(String(value))) {
        // `>>> 0` after the xor: a hash at or above 2^31 would otherwise go
        // negative and drag the whole computation into signed arithmetic.
        h = (h ^ byte) >>> 0;
        // Math.imul, not `*`: h * 16777619 reaches ~7.2e16, well past
        // Number.MAX_SAFE_INTEGER, so plain multiplication silently loses low
        // bits and diverges from PHP's exact 64-bit product.
        h = Math.imul(h, FNV_PRIME) >>> 0;
    }

    return h;
}

/** Whole seconds since the epoch, or 0 when there is no usable timestamp. */
export function epochSeconds(value) {
    if (value === null || value === undefined || value === '') return 0;
    if (typeof value === 'number' && Number.isFinite(value)) return Math.trunc(value);

    const parsed = Date.parse(value);
    if (Number.isNaN(parsed)) {
        const numeric = Number(value);
        return Number.isFinite(numeric) ? Math.trunc(numeric) : 0;
    }

    return Math.floor(parsed / 1000);
}

export function member(id, updatedAt) {
    return hash(`${id}:${epochSeconds(updatedAt)}`);
}

/**
 * @param {Array<{id: any, updated_at?: any}>} rows
 * @returns {{count: number, checksum: number}}
 */
export function checksumRows(rows) {
    let sum = 0;
    let count = 0;

    for (const row of rows ?? []) {
        if (row?.id == null) continue;
        sum = (sum + member(row.id, row.updated_at)) % MOD;
        count += 1;
    }

    return { count, checksum: sum };
}

export default { hash, epochSeconds, member, checksumRows };
